import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';

interface Subscription {
  id: string;
  tier: string;
  status: string;
  provider: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
}

const BillingEnhanced: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [onTrial, setOnTrial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await apiService.get('/billing/subscription');
      setSubscription(response.data.subscription);
      setOnTrial(response.data.onTrial || false);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessing(true);
      const response = await apiService.post('/billing/portal');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to open portal:', error);
      alert('Failed to open billing portal');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You can continue using it until the end of your billing period.')) {
      return;
    }

    try {
      setProcessing(true);
      await apiService.post('/billing/cancel', { immediately: false });
      await loadSubscription();
      alert('Subscription cancelled successfully. You can continue using it until the end of your billing period.');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setProcessing(true);
      await apiService.post('/billing/resume');
      await loadSubscription();
      alert('Subscription resumed successfully!');
    } catch (error: any) {
      console.error('Failed to resume subscription:', error);
      alert(error.response?.data?.error || 'Failed to resume subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getProviderIcon = (provider: string): string => {
    switch (provider) {
      case 'stripe':
        return '💳';
      case 'razorpay':
        return '🇮🇳';
      case 'shopify':
        return '🛍️';
      default:
        return '💰';
    }
  };

  const getProviderName = (provider: string): string => {
    switch (provider) {
      case 'stripe':
        return 'Stripe';
      case 'razorpay':
        return 'Razorpay';
      case 'shopify':
        return 'Shopify Billing';
      default:
        return provider;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDaysRemaining = (date: string): number => {
    const days = Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No subscription
  if (!subscription) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
          <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">No Active Subscription</h2>
          <p className="text-gray-600 mb-8">Start a subscription to unlock all features</p>
          <Link
            to="/pricing"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            View Pricing Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
      </div>

      {/* Current Subscription */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
            <p className="text-gray-600 capitalize mt-1">{subscription.tier} Plan</p>
          </div>
          <div className="text-right">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(subscription.status)}`}>
              {subscription.status}
            </span>
          </div>
        </div>

        {/* Trial Banner */}
        {onTrial && subscription.trialEnd && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Free Trial Active</h3>
                <p className="text-blue-800 text-sm">
                  Your trial ends in <strong>{calculateDaysRemaining(subscription.trialEnd)} days</strong> on{' '}
                  {new Date(subscription.trialEnd).toLocaleDateString()}. You won't be charged until then.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Banner */}
        {subscription.cancelAtPeriodEnd && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">Subscription Ending</h3>
                <p className="text-yellow-800 text-sm mb-3">
                  Your subscription will end on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}.
                  You can continue using it until then.
                </p>
                <button
                  onClick={handleResumeSubscription}
                  disabled={processing}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Resume Subscription'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Details */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              {getProviderIcon(subscription.provider)}
              <span className="ml-2">Payment Method</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {getProviderName(subscription.provider)}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Current Period</div>
            <div className="text-lg font-semibold text-gray-900">
              {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{' '}
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Next Billing Date</div>
            <div className="text-lg font-semibold text-gray-900">
              {subscription.cancelAtPeriodEnd
                ? 'Cancelled'
                : new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleManageSubscription}
            disabled={processing}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {processing ? 'Loading...' : 'Manage Payment Method'}
          </button>

          <Link
            to="/pricing"
            className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition inline-block"
          >
            Change Plan
          </Link>

          {!subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
            <button
              onClick={handleCancelSubscription}
              disabled={processing}
              className="bg-red-50 text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-100 transition disabled:opacity-50"
            >
              {processing ? 'Processing...' : 'Cancel Subscription'}
            </button>
          )}
        </div>
      </div>

      {/* Plan Features */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Plan Includes</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {getPlanFeatures(subscription.tier).map((feature, index) => (
            <div key={index} className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Billing History</h2>
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600">No billing history available yet</p>
          <p className="text-sm text-gray-500 mt-1">Invoices will appear here after your first payment</p>
        </div>
      </div>
    </div>
  );
};

// Helper function to get plan features
const getPlanFeatures = (tier: string): string[] => {
  switch (tier.toLowerCase()) {
    case 'starter':
      return [
        'Up to 500 products',
        '3 languages',
        'AI auto-translation',
        'Email support',
        'SEO optimization',
        'Basic analytics',
      ];
    case 'pro':
      return [
        'Up to 5,000 products',
        '10 languages',
        'AI auto-translation',
        'Priority support',
        'Translation memory',
        'Team collaboration',
        'Custom glossaries',
        'Advanced analytics',
      ];
    case 'enterprise':
      return [
        'Unlimited products',
        'Unlimited languages',
        'Dedicated account manager',
        '24/7 phone support',
        'Custom integrations',
        'SLA guarantee',
        'API access',
        'White-label options',
      ];
    default:
      return [];
  }
};

export default BillingEnhanced;
